import { baseApi as api } from 'shared/api/baseApi';
import { CATEGORIES, TRAFFICS } from 'shared/consts';
import { IMetaInterface } from 'shared/typings';
import { getOptionsFromSlug } from 'shared/utils';

const injectedRtkApi = api.injectEndpoints({
	endpoints: (build) => ({
		appConfigRetrieve: build.query<AppConfigRetrieveApiResponse, AppConfigRetrieveApiArg>({
			query: () => ({ url: `/api/app_config/` }),
		}),
		metaRetrieve: build.query<IMetaInterface, MetaRetrieveApiArg>({
			query: () => ({ url: `/api/meta/` }),
			// MANUAL TRANSFORM_RESPONSE - DO NOT DELETE
			transformResponse: (data: MetaRetrieveApiResponse): IMetaInterface => ({
				balanceCurrencies: data.balance_currencies
					.map(({ code, sign }) => ({
						value: code,
						label: `${code}, ${sign}`,
					}))
					.sort((a, b) => a.label?.localeCompare(b.label)),
				categoryOptions: getOptionsFromSlug(data.categories, CATEGORIES),
				countryOptions: data.countries
					.map(({ alpha_2, name }) => ({
						value: alpha_2,
						label: name,
					}))
					.sort((a, b) => a.label?.localeCompare(b.label)),
				currenciesMap: data.balance_currencies.reduce((acc, { code, sign }) => {
					return { ...acc, [code]: sign };
				}, {}),
				trafficTypeOptions: getOptionsFromSlug(data.traffic_types, TRAFFICS),
			}),
		}),
		metaOffersList: build.query<MetaOffersListApiResponse, MetaOffersListApiArg>({
			query: (queryArg) => ({
				url: `/api/meta/offers/`,
				params: {
					limit: queryArg.limit,
					offset: queryArg.offset,
					ordering: queryArg.ordering,
					uuid: queryArg.uuid,
				},
			}),
		}),
	}),
	overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type AppConfigRetrieveApiResponse = /** status 200  */ AppConfigRead;
export type AppConfigRetrieveApiArg = void;
export type MetaRetrieveApiResponse = /** status 200  */ Meta;
export type MetaRetrieveApiArg = void;
export type MetaOffersListApiResponse = /** status 200  */ PaginatedOfferMetaListRead;
export type MetaOffersListApiArg = {
	/** Number of results to return per page. */
	limit?: number;
	/** The initial index from which to return the results. */
	offset?: number;
	/** Which field to use when ordering the results. */
	ordering?: string;
	uuid?: string[];
};
export type Balance = {
	currency: string;
};
export type BalanceRead = {
	balance_amount: string;
	on_hold_amount: string;
	approved_amount: string;
	currency: string;
};
export type User = {
	user_id: string;
	email: string;
	is_banned: boolean;
	balance: Balance | null;
	language: string;
	roles?: string[];
};
export type UserRead = {
	user_id: string;
	email: string;
	is_banned: boolean;
	balance: BalanceRead | null;
	language: string;
	roles?: string[];
};
export type AppConfig = {
	user: User | null;
	is_super_login: boolean;
	payouts_available: boolean;
};
export type AppConfigRead = {
	user: UserRead | null;
	is_super_login: boolean;
	payouts_available: boolean;
};
export type CategoryMeta = {
	slug: string;
	name: string;
};
export type CurrencyMeta = {
	code: string;
	sign: string;
};
export type CountryMeta = {
	alpha_2: string;
	name: string;
};
export type TrafficTypeMeta = {
	slug: string;
	name: string;
};
export type Meta = {
	categories: CategoryMeta[];
	currencies: CurrencyMeta[];
	balance_currencies: CurrencyMeta[];
	countries: CountryMeta[];
	traffic_types: TrafficTypeMeta[];
};
export type OfferMeta = {
	slug: string;
	name: string;
};
export type OfferMetaRead = {
	uuid: string;
	slug: string;
	name: string;
};
export type PaginatedOfferMetaList = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: OfferMeta[];
};
export type PaginatedOfferMetaListRead = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: OfferMetaRead[];
};
